import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  DataLayerContextValue,
  DataLayerProviderProps,
  DataLayerState,
  DataLayerActions,
  DataLayerConfig,
  DataLayerLegendItem,
  DataLayerEvent,
  DataLayerEventType,
} from '@/types/shared/data-layers.types';
import {
  initializeDataLayerState,
  mergeDataLayerStates,
  toggleLegendItem,
  setLegendItemsForGraph,
  createDataLayerEvent,
  serializeDataLayerState,
  deserializeDataLayerState,
} from '../utils/shared/dataLayers.utils';

export const DataLayerContext = createContext<DataLayerContextValue | null>(null);

type DataLayerAction =
  | { type: 'SELECT_LAYER'; payload: string }
  | { type: 'DESELECT_LAYER'; payload: string }
  | { type: 'SELECT_GRAPH'; payload: string }
  | { type: 'DESELECT_GRAPH'; payload: string }
  | { type: 'SET_FILTER_VALUE'; payload: { filterId: string; value: any } }
  | { type: 'RESET_FILTERS' }
  | { type: 'TOGGLE_LEGEND_ITEM'; payload: { graphId: string; itemId: string } }
  | { type: 'SET_LEGEND_ITEMS'; payload: { graphId: string; items: DataLayerLegendItem[] } }
  | { type: 'SET_LOADING'; payload: { key: string; loading: boolean } }
  | { type: 'SET_ERROR'; payload: { key: string; error: Error | null } }
  | { type: 'SET_DATA'; payload: { key: string; data: any } }
  | { type: 'RESET' }
  | { type: 'SET_STATE'; payload: Partial<DataLayerState> };

function dataLayerReducer(state: DataLayerState, action: DataLayerAction): DataLayerState {
  switch (action.type) {
    case 'SELECT_LAYER':
      if (state.selectedDataLayers.includes(action.payload)) {
        return state;
      }
      return {
        ...state,
        selectedDataLayers: [...state.selectedDataLayers, action.payload],
      };

    case 'DESELECT_LAYER':
      return {
        ...state,
        selectedDataLayers: state.selectedDataLayers.filter((id: string) => id !== action.payload),
      };

    case 'SELECT_GRAPH':
      if (state.selectedGraphs.includes(action.payload)) {
        return state;
      }
      return {
        ...state,
        selectedGraphs: [...state.selectedGraphs, action.payload],
      };

    case 'DESELECT_GRAPH':
      return {
        ...state,
        selectedGraphs: state.selectedGraphs.filter((id: string) => id !== action.payload),
      };

    case 'SET_FILTER_VALUE':
      return {
        ...state,
        filterValues: {
          ...state.filterValues,
          [action.payload.filterId]: action.payload.value,
        },
      };

    case 'RESET_FILTERS':
      return {
        ...state,
        filterValues: {},
      };

    case 'TOGGLE_LEGEND_ITEM':
      return {
        ...state,
        legendStates: toggleLegendItem(
          state.legendStates,
          action.payload.graphId,
          action.payload.itemId
        ),
      };

    case 'SET_LEGEND_ITEMS':
      return {
        ...state,
        legendStates: setLegendItemsForGraph(
          state.legendStates,
          action.payload.graphId,
          action.payload.items
        ),
      };

    case 'SET_LOADING':
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.payload.key]: action.payload.loading,
        },
      };

    case 'SET_ERROR':
      return {
        ...state,
        errorStates: {
          ...state.errorStates,
          [action.payload.key]: action.payload.error,
        },
      };

    case 'SET_DATA':
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.key]: action.payload.data,
        },
      };

    case 'RESET':
      return initializeDataLayerState();

    case 'SET_STATE':
      return mergeDataLayerStates(state, action.payload);

    default:
      return state;
  }
}

export function DataLayerProvider({
  metadata,
  initialState,
  config = {},
  children,
  onStateChange,
}: DataLayerProviderProps) {
  const [state, dispatch] = useReducer(
    dataLayerReducer,
    initializeDataLayerState(metadata || undefined, initialState)
  );

  const eventHandlersRef = useRef<Map<DataLayerEventType, Set<Function>>>(new Map());
  const stateRef = useRef(state);
  stateRef.current = state;

  const defaultConfig: DataLayerConfig = useMemo(
    () => ({
      enableGraphManagement: true,
      enableFilterReset: true,
      enableDataExport: false,
      enableSearch: true,
      enablePagination: false,
      defaultLayout: 'tabs',
      ...config,
    }),
    [config]
  );

  const emitEvent = useCallback((type: DataLayerEventType, payload: any, metadata?: Record<string, any>) => {
    const event = createDataLayerEvent(type, payload, metadata);
    const handlers = eventHandlersRef.current.get(type);
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }, []);

  const addEventListener = useCallback((type: DataLayerEventType, handler: Function) => {
    if (!eventHandlersRef.current.has(type)) {
      eventHandlersRef.current.set(type, new Set());
    }
    eventHandlersRef.current.get(type)?.add(handler);

    return () => {
      eventHandlersRef.current.get(type)?.delete(handler);
    };
  }, []);

  const actions: DataLayerActions = useMemo(
    () => ({
      selectDataLayer: (layerId: string) => {
        dispatch({ type: 'SELECT_LAYER', payload: layerId });
        emitEvent('layer:select', { layerId });
      },
      deselectDataLayer: (layerId: string) => {
        dispatch({ type: 'DESELECT_LAYER', payload: layerId });
        emitEvent('layer:deselect', { layerId });
      },
      selectGraph: (graphId: string) => {
        dispatch({ type: 'SELECT_GRAPH', payload: graphId });
        emitEvent('graph:select', { graphId });
      },
      deselectGraph: (graphId: string) => {
        dispatch({ type: 'DESELECT_GRAPH', payload: graphId });
        emitEvent('graph:deselect', { graphId });
      },
      setFilterValue: (filterId: string, value: any) => {
        dispatch({ type: 'SET_FILTER_VALUE', payload: { filterId, value } });
        emitEvent('filter:change', { filterId, value });
      },
      resetFilters: () => {
        dispatch({ type: 'RESET_FILTERS' });
        emitEvent('filter:reset', {});
      },
      toggleLegendItem: (graphId: string, itemId: string) => {
        dispatch({ type: 'TOGGLE_LEGEND_ITEM', payload: { graphId, itemId } });
        emitEvent('legend:toggle', { graphId, itemId });
      },
      setLegendItems: (graphId: string, items: DataLayerLegendItem[]) => {
        dispatch({ type: 'SET_LEGEND_ITEMS', payload: { graphId, items } });
      },
      setLoading: (key: string, loading: boolean) => {
        dispatch({ type: 'SET_LOADING', payload: { key, loading } });
      },
      setError: (key: string, error: Error | null) => {
        dispatch({ type: 'SET_ERROR', payload: { key, error } });
        if (error) {
          emitEvent('data:error', { key, error: error.message });
        }
      },
      setData: (key: string, data: any) => {
        dispatch({ type: 'SET_DATA', payload: { key, data } });
        emitEvent('data:success', { key, data });
      },
      reset: () => {
        dispatch({ type: 'RESET' });
      },
    }),
    [emitEvent]
  );

  useEffect(() => {
    if (onStateChange) {
      onStateChange(state);
    }
  }, [state, onStateChange]);

  useEffect(() => {
    if (defaultConfig.stateManagement?.persistState) {
      const storageKey = defaultConfig.stateManagement.storageKey || 'dataLayerState';
      const storageType = defaultConfig.stateManagement.storageType || 'local';

      const storage = storageType === 'session' ? sessionStorage : localStorage;

      try {
        storage.setItem(storageKey, serializeDataLayerState(state));
      } catch (error) {
        console.error('Failed to persist data layer state:', error);
      }
    }
  }, [state, defaultConfig.stateManagement]);

  useEffect(() => {
    if (defaultConfig.stateManagement?.persistState) {
      const storageKey = defaultConfig.stateManagement.storageKey || 'dataLayerState';
      const storageType = defaultConfig.stateManagement.storageType || 'local';

      const storage = storageType === 'session' ? sessionStorage : localStorage;

      try {
        const stored = storage.getItem(storageKey);
        if (stored) {
          const restoredState = deserializeDataLayerState(stored, state);
          dispatch({ type: 'SET_STATE', payload: restoredState });
        }
      } catch (error) {
        console.error('Failed to restore data layer state:', error);
      }
    }
  }, []);

  const contextValue: DataLayerContextValue = useMemo(
    () => ({
      metadata: metadata || null,
      state,
      actions,
      config: defaultConfig,
    }),
    [metadata, state, actions, defaultConfig]
  );

  return <DataLayerContext.Provider value={contextValue}>{children}</DataLayerContext.Provider>;
}